import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Calendar, RefreshCw, ChevronRight, AlertCircle, TrendingUp } from 'lucide-react';
import { useAuth } from './AuthContext';
import { API_URL } from '../api/config';

const ViewSubscriptionInfo = () => {
  const { userToken: token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubscriptionDetails();
  }, [token]);

  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch subscription details');
      }
      
      const data = await response.json();
      setSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription details:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePlan = () => {
    navigate('/subscription');
  };

  const getDaysRemaining = () => {
    if (!subscription || !subscription.endDate) return 0;
    
    const endDate = new Date(subscription.endDate);
    const today = new Date();
    const diffTime = endDate - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getPlanName = (planCode) => {
    switch(planCode) {
      case '1-month': return 'Monthly Premium';
      case '6-months': return '6-Month Premium';
      case '1-year': return 'Annual Premium';
      default: return planCode;
    }
  };

  const getPlanPrice = (planCode) => {
    switch(planCode) {
      case '1-month': return '₹1/month';
      case '6-months': return '₹5 for 6 months';
      case '1-year': return '₹10/year';
      default: return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 text-lg">Loading subscription information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-red-500 mb-4 text-xl">Error: {error}</div>
          <button 
            onClick={fetchSubscriptionDetails}
            className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg shadow hover:bg-indigo-700 transition duration-200"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!subscription || subscription.status !== 'active') {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">No Active Subscription</h2>
          <div className="bg-indigo-50 p-4 rounded-lg mb-6">
            <AlertCircle className="text-indigo-600 mx-auto mb-2" size={32} />
            <p className="text-gray-700">
              You don't have an active subscription. Subscribe now to access premium features.
            </p>
          </div>
          <button 
            onClick={handleChangePlan}
            className="w-full px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg shadow hover:bg-indigo-700 transition duration-200"
          >
            Subscribe Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 text-center mb-8">Your Subscription</h1>

        <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
          <div className="bg-indigo-600 text-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">{getPlanName(subscription.plan)}</h2>
              <div className="bg-indigo-500 p-2 rounded-full">
                <CreditCard size={24} />
              </div>
            </div>
            <p className="mt-2 opacity-90">Status: Active</p>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start mb-2">
                  <Calendar className="text-indigo-600 mr-3 mt-1" size={20} />
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Expiry Date</h3>
                    <p className="text-gray-800 font-medium">{formatDate(subscription.endDate)}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-indigo-50 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <TrendingUp className="text-indigo-600 mr-3" size={20} />
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Current Plan</h3>
                  <p className="text-indigo-800 font-medium">{getPlanName(subscription.plan)} ({getPlanPrice(subscription.plan)})</p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-100 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <RefreshCw className="text-green-600 mr-3" size={20} />
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Time Remaining</h3>
                    <p className="text-green-700 font-medium">{getDaysRemaining()} days</p>
                  </div>
                </div>
                <div className="bg-green-100 h-2 rounded-full w-32 overflow-hidden">
                  <div 
                    className="bg-green-500 h-full" 
                    style={{ 
                      width: `${
                        subscription.plan === '1-month' ? 
                        (getDaysRemaining() / 30) * 100 : 
                        subscription.plan === '6-months' ? 
                        (getDaysRemaining() / 180) * 100 : 
                        (getDaysRemaining() / 365) * 100
                      }%` 
                    }}
                  ></div>
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Subscription Benefits</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <div className="text-indigo-600 mr-2">•</div>
                  <p className="text-gray-600">Access to Expense and Sales reports</p>
                </li>
                <li className="flex items-start">
                  <div className="text-indigo-600 mr-2">•</div>
                  <p className="text-gray-600">Weekly and Monthly analytics</p>
                </li>
                <li className="flex items-start">
                  <div className="text-indigo-600 mr-2">•</div>
                  <p className="text-gray-600">Data visualization and insights</p>
                </li>
                <li className="flex items-start">
                  <div className="text-indigo-600 mr-2">•</div>
                  <p className="text-gray-600">Historical data access</p>
                </li>
              </ul>

              <button 
                onClick={handleChangePlan}
                className="w-full flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
              >
                Change Plan
                <ChevronRight size={18} className="ml-1" />
              </button>
            </div>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex">
            <AlertCircle className="text-amber-700 mr-2 flex-shrink-0" size={20} />
            <div>
              <p className="text-amber-800 mb-1">• For any issues with your subscription, please contact support</p>
              <p className="text-amber-800">• All premium features are available throughout your subscription period</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewSubscriptionInfo;